// Announcements from the platform's admins: a banner at the top of the app (and on the sign-in page). Info and
// warnings can be dismissed (remembered in this browser); critical ones stay until they end.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { cx } from "./ui";

export type Announcement = { id: string; message: string; level: "info" | "warning" | "critical"; linkUrl: string | null; linkText: string | null; endsAt: string | null };
const KEY = "collab:dismissed-announcements";
const readDismissed = (): string[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? "[]") as string[]; } catch { return []; } };
const STYLE = {
  info: "bg-brand-50 text-brand-800 ring-brand-200 dark:bg-brand-700/25 dark:text-brand-100 dark:ring-brand-700/40",
  warning: "bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:ring-amber-800",
  critical: "bg-red-600 text-white ring-red-700",
};

function Banners({ items }: { items: Announcement[] }) {
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const shown = items.filter((a) => a.level === "critical" || !dismissed.includes(a.id));
  if (!shown.length) return null;
  function dismiss(id: string) {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  }
  return (
    <div className="space-y-px" role="region" aria-label="Announcements">
      {shown.map((a) => (
        <div key={a.id} role={a.level === "critical" ? "alert" : "status"} className={cx("flex items-start gap-3 px-4 py-2 text-sm ring-1", STYLE[a.level])}>
          <p className="min-w-0 flex-1">
            {a.message}
            {a.linkUrl && <> <a href={a.linkUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline">{a.linkText ?? "Learn more"}</a></>}
          </p>
          {a.level !== "critical" && <button type="button" onClick={() => dismiss(a.id)} aria-label="Dismiss announcement" className="rounded px-1 opacity-70 hover:opacity-100">×</button>}
        </div>
      ))}
    </div>
  );
}

// Inside the app: everyone's announcements and those for the person's organizations; checked every 5 minutes.
export function AnnouncementBanner() {
  const q = useQuery({ queryKey: ["announcements"], queryFn: () => api<{ announcements: Announcement[] }>("/announcements"), refetchInterval: 5 * 60_000, staleTime: 60_000 });
  return <Banners items={q.data?.announcements ?? []} />;
}

// The sign-in page: announcements meant for it (maintenance, outages).
export function SignInAnnouncements() {
  const q = useQuery({ queryKey: ["announcements-public"], queryFn: () => api<{ announcements: Announcement[] }>("/announcements/public"), staleTime: 60_000 });
  const items = q.data?.announcements ?? [];
  if (!items.length) return null;
  return <div className="fixed inset-x-0 top-0 z-30"><Banners items={items} /></div>;
}
